import type { IEmailTemplateRepository, EmailTemplate, TemplateVariable } from '../interfaces/IEmailTemplateRepository';
import { simulateDelay, maybeSimulateError } from './mockData';

const mockTemplates: EmailTemplate[] = [
  {
    id: 'template-1',
    name: 'Standard Reminder',
    subject: 'Action Required: Complete Your {{tenant_name}} Onboarding',
    body: `Hello {{account_manager_name}},

We noticed that the onboarding process for {{tenant_name}} has been in progress for {{days_stalled}} days.

To continue, please complete the following steps:
{{missing_roles}}

If you need any assistance, please don't hesitate to reach out to our support team.

Best regards,
The Platform Team`,
    template_type: 'reminder',
    variables: ['tenant_name', 'account_manager_name', 'days_stalled', 'missing_roles'],
    created_at: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString(),
    updated_at: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000).toISOString(),
    created_by: 'admin-1',
    is_active: true,
    usage_count: 45,
  },
  {
    id: 'template-2',
    name: 'Urgent Escalation',
    subject: 'URGENT: {{tenant_name}} Onboarding Requires Immediate Attention',
    body: `Dear {{account_manager_name}},

This is an urgent notice regarding the onboarding process for {{tenant_name}}.

The process has been stalled for {{days_stalled}} days, which exceeds our recommended completion timeframe.

Outstanding items:
{{missing_roles}}

Please complete these steps within the next 48 hours to avoid any service disruptions.

For immediate assistance, contact us at support@platform.com

Urgent regards,
Platform Operations Team`,
    template_type: 'escalation',
    variables: ['tenant_name', 'account_manager_name', 'days_stalled', 'missing_roles'],
    created_at: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString(),
    updated_at: new Date(Date.now() - 10 * 24 * 60 * 60 * 1000).toISOString(),
    created_by: 'admin-1',
    is_active: true,
    usage_count: 12,
  },
  {
    id: 'template-3',
    name: 'Welcome Email',
    subject: 'Welcome to {{tenant_name}}!',
    body: `Welcome {{account_manager_name}},

Thank you for starting your onboarding journey with us!

We're excited to have {{tenant_name}} on our platform. Your onboarding progress is currently at {{progress}}%.

Next steps:
- Complete your profile
- Set up your team
- Configure your preferences

Let's get started!

Best regards,
The Platform Team`,
    template_type: 'welcome',
    variables: ['tenant_name', 'account_manager_name', 'progress'],
    created_at: new Date(Date.now() - 60 * 24 * 60 * 60 * 1000).toISOString(),
    updated_at: new Date(Date.now() - 60 * 24 * 60 * 60 * 1000).toISOString(),
    created_by: 'admin-1',
    is_active: true,
    usage_count: 89,
  },
];

const availableVariables: TemplateVariable[] = [
  { key: 'tenant_name', label: 'Tenant Name', description: 'The name of the organization', example: 'Acme Corporation' },
  { key: 'account_manager_name', label: 'Account Manager Name', description: 'Name of the account manager', example: 'John Smith' },
  { key: 'account_manager_email', label: 'Account Manager Email', description: 'Email of the account manager', example: 'john@acme.com' },
  { key: 'days_stalled', label: 'Days Stalled', description: 'Number of days since onboarding started', example: '15' },
  { key: 'missing_roles', label: 'Missing Roles', description: 'List of incomplete onboarding roles', example: 'Finance Manager, Account Owner' },
  { key: 'progress', label: 'Progress Percentage', description: 'Overall onboarding progress', example: '75' },
  { key: 'last_activity', label: 'Last Activity', description: 'Date of last activity', example: '2024-01-15' },
  { key: 'support_email', label: 'Support Email', description: 'Platform support email', example: 'support@platform.com' },
];

export class MockEmailTemplateRepository implements IEmailTemplateRepository {
  private templates: EmailTemplate[] = [...mockTemplates];

  async getAll(): Promise<EmailTemplate[]> {
    await simulateDelay();
    maybeSimulateError();
    return [...this.templates].sort((a, b) => 
      new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime()
    );
  }

  async getById(id: string): Promise<EmailTemplate | null> {
    await simulateDelay();
    maybeSimulateError();
    return this.templates.find(t => t.id === id) || null;
  }

  async getByType(type: EmailTemplate['template_type']): Promise<EmailTemplate[]> {
    await simulateDelay();
    maybeSimulateError();
    return this.templates.filter(t => t.template_type === type && t.is_active);
  }

  async getAvailableVariables(): Promise<TemplateVariable[]> {
    await simulateDelay();
    return [...availableVariables];
  }

  async create(data: {
    name: string;
    subject: string;
    body: string;
    template_type: EmailTemplate['template_type'];
    variables: string[];
    created_by: string;
  }): Promise<EmailTemplate> {
    await simulateDelay();
    maybeSimulateError();

    const template: EmailTemplate = {
      id: `template-${Date.now()}`,
      ...data,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
      is_active: true,
      usage_count: 0,
    };

    this.templates.push(template);
    return template;
  }

  async update(id: string, data: Partial<Omit<EmailTemplate, 'id' | 'created_at' | 'created_by'>>): Promise<EmailTemplate> {
    await simulateDelay();
    maybeSimulateError();

    const index = this.templates.findIndex(t => t.id === id);
    if (index === -1) throw new Error('Template not found');

    this.templates[index] = {
      ...this.templates[index],
      ...data,
      updated_at: new Date().toISOString(),
    };

    return this.templates[index];
  }

  async delete(id: string): Promise<void> {
    await simulateDelay();
    maybeSimulateError();

    const index = this.templates.findIndex(t => t.id === id);
    if (index === -1) throw new Error('Template not found');

    this.templates.splice(index, 1);
  }

  async preview(
    template: { subject: string; body: string; variables: string[] },
    sampleData: Record<string, string>
  ): Promise<{ subject: string; body: string }> {
    await simulateDelay();

    let subject = template.subject;
    let body = template.body;

    // Replace all variables with sample data
    Object.entries(sampleData).forEach(([key, value]) => {
      const regex = new RegExp(`{{${key}}}`, 'g');
      subject = subject.replace(regex, value);
      body = body.replace(regex, value);
    });

    return { subject, body };
  }
}
